// ============================================
// src/FraudDetection.js
// Compares new signup fingerprint against
// existing records. Returns risk score + action.
// ============================================

import {
  collection, doc, setDoc, getDocs,
  query, where, serverTimestamp, updateDoc
} from 'firebase/firestore';
import { db } from './firebase';

// ── Risk thresholds ────────────────────────────
const RISK = {
  EXACT_BLOCK:  90,  // Block signup
  HIGH:         70,  // Flag + require email verify
  MEDIUM:       40,  // Flag for admin review
  LOW:           0,  // Allow, just record
};

// ── Calculate risk score ───────────────────────
// Returns 0-100. Higher = more suspicious.
const calculateRiskScore = (newFp, existingFp) => {
  let score = 0;
  const matches = [];
  const mismatches = [];

  // ── Hard signals (high weight) ──────────────

  // Same persistent device ID = almost certainly same device
  if (newFp.deviceId && existingFp.deviceId && newFp.deviceId === existingFp.deviceId) {
    score += 50;
    matches.push('deviceId');
  }

  // Same canvas hash = same GPU + fonts
  if (newFp.canvasHash && existingFp.canvasHash && 
      newFp.canvasHash === existingFp.canvasHash && 
      newFp.canvasHash !== 'canvas_blocked') {
    score += 25;
    matches.push('canvasHash');
  }

  // Same WebGL renderer = same GPU
  if (newFp.webglHash && existingFp.webglHash && 
      newFp.webglHash === existingFp.webglHash && 
      newFp.webglHash !== 'webgl_blocked' &&
      newFp.webglHash !== 'webgl_not_supported') {
    score += 20;
    matches.push('webglHash');
  }

  // Same IP address
  if (newFp.ipAddress && existingFp.ipAddress && 
      newFp.ipAddress === existingFp.ipAddress && 
      newFp.ipAddress !== 'unknown') {
    score += 15;
    matches.push('ipAddress');
  }

  // ── Medium signals (lower weight) ───────────

  if (newFp.screenResolution === existingFp.screenResolution) {
    score += 5;
    matches.push('screenResolution');
  }

  if (newFp.timezone === existingFp.timezone) {
    score += 3;
    matches.push('timezone');
  }

  if (newFp.hardwareConcurrency === existingFp.hardwareConcurrency && 
      newFp.hardwareConcurrency > 0) {
    score += 3;
    matches.push('hardwareConcurrency');
  }

  if (newFp.deviceMemory === existingFp.deviceMemory && 
      newFp.deviceMemory > 0) {
    score += 3;
    matches.push('deviceMemory');
  }

  if (newFp.platform === existingFp.platform && newFp.platform) {
    score += 3;
    matches.push('platform');
  }

  if (newFp.colorDepth === existingFp.colorDepth && newFp.colorDepth > 0) {
    score += 2;
    matches.push('colorDepth');
  }

  // ── Negative signals (reduce score) ─────────

  // Different IP can mean VPN - don't reduce much
  if (newFp.ipAddress !== existingFp.ipAddress && 
      newFp.ipAddress !== 'unknown' && 
      existingFp.ipAddress !== 'unknown') {
    score -= 5;
    mismatches.push('ipAddress');
  }

  // Cap at 100
  return {
    score: Math.min(100, Math.max(0, score)),
    matches,
    mismatches,
  };
};

// ── Get risk action from score ─────────────────
const getRiskAction = (score) => {
  if (score >= RISK.EXACT_BLOCK) return 'blocked';
  if (score >= RISK.HIGH)        return 'flagged_high';
  if (score >= RISK.MEDIUM)      return 'flagged_medium';
  return 'allowed';
};

// ── MAIN: Check fingerprint before signup ──────
export const checkFingerprintBeforeSignup = async (newFingerprint) => {
  try {
    console.log('🔍 Running fraud check for new signup...');

    // Query all existing fingerprints
    const fpSnapshot = await getDocs(collection(db, 'deviceFingerprints'));

    let highestRisk = { score: 0, matches: [], mismatches: [] };
    let matchedUserId = null;
    let matchedFingerprintId = null;

    for (const fpDoc of fpSnapshot.docs) {
      const existingFp = fpDoc.data();

      // Skip if same user (login on same device)
      // Can't check userId here since user doesn't exist yet

      const risk = calculateRiskScore(newFingerprint, existingFp);

      if (risk.score > highestRisk.score) {
        highestRisk = risk;
        matchedUserId = existingFp.userId;
        matchedFingerprintId = fpDoc.id;
      }
    }

    const action = getRiskAction(highestRisk.score);

    console.log(`🎯 Fraud check result: score=${highestRisk.score}, action=${action}`);
    console.log(`   Matched signals:`, highestRisk.matches);

    return {
      success: true,
      riskScore: highestRisk.score,
      action,
      matchedUserId,
      matchedFingerprintId,
      matchedSignals: highestRisk.matches,
      allowed: action !== 'blocked',
      message: action === 'blocked'
        ? 'An account already exists on this device. Each device can only have one account. Please log in to your existing account or contact support.'
        : null,
    };
  } catch (error) {
    console.error('Fraud check error:', error);
    // On error, allow signup but flag it
    return {
      success: false,
      riskScore: 0,
      action: 'allowed',
      allowed: true,
      error: error.message,
    };
  }
};

// Same Railway base URL serverApi.js uses. Not exported from that file, so
// the literal is duplicated here rather than modifying it (out of scope for
// this change) - keep both in sync if it's ever updated.
const SERVER_URL = 'https://vibely-server-production-8a60.up.railway.app';

// ── Count existing accounts on this device ─────
// Used to enforce "max 3 accounts per device" at signup.
// Goes through the server (Admin SDK) instead of querying Firestore
// directly - deviceFingerprints is admin-read-only under the locked rules,
// and this runs BEFORE the account exists, so there's no ID token to send
// yet either. Counts by deviceId (the persistent per-browser id), not the
// hardware fingerprintHash. Never throws - callers should only block on
// { success: true, count >= 3 }; treat success:false as "unknown, don't
// block" (fail-open).
export const countAccountsForDevice = async (deviceId) => {
  try {
    if (!deviceId) return { success: true, count: 0 };

    const resp = await fetch(`${SERVER_URL}/count-device-accounts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId })
    });

    const data = await resp.json();

    if (!resp.ok) {
      return { success: false, count: 0, error: data.error || 'Count check failed' };
    }

    return { success: true, count: data.count || 0 };
  } catch (error) {
    console.error('Count accounts for device error:', error);
    return { success: false, count: 0, error: error.message };
  }
};

// ── Save fingerprint after successful signup ───
export const saveFingerprintForUser = async (userId, fingerprint, riskData) => {
  try {
    const fpRef = doc(collection(db, 'deviceFingerprints'));
    await setDoc(fpRef, {
      id: fpRef.id,
      userId,
      deviceId: fingerprint.deviceId,
      ipAddress: fingerprint.ipAddress,
      fingerprintHash: fingerprint.fingerprintHash,
      canvasHash: fingerprint.canvasHash,
      webglHash: fingerprint.webglHash,
      audioHash: fingerprint.audioHash,
      userAgent: fingerprint.userAgent,
      platform: fingerprint.platform,
      screenResolution: fingerprint.screenResolution,
      colorDepth: fingerprint.colorDepth,
      timezone: fingerprint.timezone,
      language: fingerprint.language,
      hardwareConcurrency: fingerprint.hardwareConcurrency,
      deviceMemory: fingerprint.deviceMemory,
      maxTouchPoints: fingerprint.maxTouchPoints,
      pixelRatio: fingerprint.pixelRatio,
      riskScore: riskData.riskScore || 0,
      createdAt: serverTimestamp(),
      lastSeenAt: serverTimestamp(),
    });

    console.log('✅ Fingerprint saved for user:', userId);
    return { success: true, fingerprintId: fpRef.id };
  } catch (error) {
    console.error('Save fingerprint error:', error);
    return { success: false, error: error.message };
  }
};

// ── Save fraud alert ───────────────────────────
export const saveFraudAlert = async (newUserId, fingerprint, riskData) => {
  try {
    if (riskData.riskScore < 40) return; // Don't save low-risk alerts

    const alertRef = doc(collection(db, 'fraudAlerts'));
    await setDoc(alertRef, {
      id: alertRef.id,
      type: riskData.riskScore >= 90 ? 'duplicate_device' : 'suspicious_signup',
      newUserId,
      matchedUserId: riskData.matchedUserId || null,
      matchedFingerprintId: riskData.matchedFingerprintId || null,
      matchedSignals: riskData.matchedSignals || [],
      riskScore: riskData.riskScore,
      action: riskData.action,
      ipAddress: fingerprint.ipAddress,
      fingerprintHash: fingerprint.fingerprintHash,
      deviceId: fingerprint.deviceId,
      reviewed: false,
      reviewedBy: null,
      reviewedAt: null,
      createdAt: serverTimestamp(),
    });

    console.log('⚠️ Fraud alert saved, score:', riskData.riskScore);
  } catch (error) {
    console.error('Save fraud alert error:', error);
  }
};

// ── Update fingerprint last seen ───────────────
export const updateFingerprintLastSeen = async (userId, ipAddress) => {
  try {
    const q = query(
      collection(db, 'deviceFingerprints'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    
    for (const fpDoc of snapshot.docs) {
      await updateDoc(fpDoc.ref, {
        lastSeenAt: serverTimestamp(),
        ipAddress: ipAddress || fpDoc.data().ipAddress,
      });
    }
  } catch (error) {
    console.error('Update last seen error:', error);
  }
};

// ── Admin: Get all fraud alerts ─────────────────
export const getFraudAlerts = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'fraudAlerts'));
    return {
      success: true,
      alerts: snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => b.riskScore - a.riskScore),
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ── Admin: Get fingerprints for a user ──────────
export const getUserFingerprints = async (userId) => {
  try {
    const q = query(
      collection(db, 'deviceFingerprints'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    return {
      success: true,
      fingerprints: snapshot.docs.map(d => ({ id: d.id, ...d.data() })),
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ── Admin: Resolve a fraud alert ────────────────
export const resolveFraudAlert = async (alertId, adminId, resolution) => {
  try {
    await updateDoc(doc(db, 'fraudAlerts', alertId), {
      reviewed: true,
      reviewedBy: adminId,
      reviewedAt: serverTimestamp(),
      resolution, // 'false_positive' | 'confirmed_fraud' | 'banned'
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};